import React from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { Icon, Skeleton, Pagination } from "../ui";
import { useAiAgents } from "../../hooks/useAiAgents";
import useAgentTranslations, {
  AVAILABLE_LOCALES,
  extractPlaceholders,
} from "../../hooks/useAgentTranslations";

// Pagination constants - can be moved to a config file later
const TRANSLATIONS_PER_PAGE = 50;

const formatKeyLabel = (key) => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const AgentTranslationsSettingsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("agentTranslations");
  const [selectedAgentId, setSelectedAgentId] = React.useState(null);
  const [activeLocale, setActiveLocale] = React.useState("en");
  const [editedValues, setEditedValues] = React.useState({});
  const [isCopyModalOpen, setIsCopyModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);

  const { data: agentsData, isLoading: isLoadingAgents } = useAiAgents({
    pageSize: 50,
  });

  const {
    useTranslations,
    useUpdateTranslations,
    useCopyTranslations,
    useResetTranslation,
  } = useAgentTranslations();

  const { data: translations, isLoading: isLoadingTranslations } =
    useTranslations(selectedAgentId, activeLocale);

  const updateMutation = useUpdateTranslations();
  const copyMutation = useCopyTranslations();
  const resetMutation = useResetTranslation();

  const agents = agentsData?.agents || [];
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  const hasUnsavedChanges = Object.keys(editedValues).length > 0;

  const translationKeys = Object.keys(translations || {});

  // Filter translations based on search query
  const filteredTranslationKeys = React.useMemo(() => {
    if (!searchQuery.trim()) return translationKeys;

    const query = searchQuery.toLowerCase().trim();
    return translationKeys.filter((key) => {
      const translation = translations?.[key];
      const keyMatch = key.toLowerCase().includes(query);
      const formattedKeyMatch = formatKeyLabel(key)
        .toLowerCase()
        .includes(query);
      const valueMatch = translation?.value?.toLowerCase().includes(query);
      const defaultMatch = translation?.default?.toLowerCase().includes(query);
      return keyMatch || formattedKeyMatch || valueMatch || defaultMatch;
    });
  }, [translationKeys, translations, searchQuery]);

  // Pagination - simulating backend pagination structure for future compatibility
  const pagination = React.useMemo(() => {
    const total = filteredTranslationKeys.length;
    const totalPages = Math.ceil(total / TRANSLATIONS_PER_PAGE);
    return {
      total,
      total_pages: totalPages,
      page: currentPage,
      page_size: TRANSLATIONS_PER_PAGE,
    };
  }, [filteredTranslationKeys.length, currentPage]);

  // Get paginated translation keys for current page
  const paginatedTranslationKeys = React.useMemo(() => {
    const startIndex = (currentPage - 1) * TRANSLATIONS_PER_PAGE;
    const endIndex = startIndex + TRANSLATIONS_PER_PAGE;
    return filteredTranslationKeys.slice(startIndex, endIndex);
  }, [filteredTranslationKeys, currentPage]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Check if any field exceeds maxLength
  const hasValidationErrors = Object.entries(editedValues).some(
    ([key, value]) => {
      const maxLength = translations?.[key]?.maxLength || 500;
      return value && value.length > maxLength;
    },
  );

  const handleAgentChange = (e) => {
    const newAgentId = e.target.value ? parseInt(e.target.value, 10) : null;
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(t("unsaved.confirmDiscard"));
      if (!confirmed) return;
    }
    setEditedValues({});
    setCurrentPage(1);
    setSelectedAgentId(newAgentId);
  };

  const handleLocaleChange = (e) => {
    const newLocale = e.target.value;
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(t("unsaved.confirmDiscard"));
      if (!confirmed) return;
    }
    setEditedValues({});
    setCurrentPage(1);
    setActiveLocale(newLocale);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFieldChange = (key, value) => {
    setEditedValues((prev) => {
      const originalValue = translations?.[key]?.value || "";
      if (value === originalValue) {
        const newValues = { ...prev };
        delete newValues[key];
        return newValues;
      }
      return { ...prev, [key]: value };
    });
  };

  const handleResetField = (key) => {
    resetMutation.mutate(
      {
        agentId: selectedAgentId,
        locale: activeLocale,
        key,
      },
      {
        onSuccess: () => {
          setEditedValues((prev) => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
          });
        },
      },
    );
  };

  const handleSaveAll = () => {
    if (!selectedAgentId || Object.keys(editedValues).length === 0) return;

    updateMutation.mutate(
      {
        agentId: selectedAgentId,
        translations: {
          [activeLocale]: editedValues,
        },
      },
      {
        onSuccess: () => {
          setEditedValues({});
        },
      },
    );
  };

  const handleDiscardChanges = () => {
    setEditedValues({});
  };

  const handleCopyFromAgent = (sourceAgentId) => {
    if (!selectedAgentId || !sourceAgentId) return;

    copyMutation.mutate(
      {
        targetAgentId: selectedAgentId,
        sourceAgentId,
      },
      {
        onSuccess: () => {
          setIsCopyModalOpen(false);
          setEditedValues({});
        },
      },
    );
  };

  const getAgentLabel = (agent) => {
    const provider = agent.provider || t("labels.unknownProvider");
    const identifier =
      agent.provider_external_id ||
      agent.site?.domain ||
      t("labels.agentWithId", { id: agent.id });
    return `${provider.charAt(0).toUpperCase() + provider.slice(1)} - ${identifier}`;
  };

  return (
    <div>
      <PageHeader
        title={t("header.title")}
        subtitle={t("header.subtitle")}
        selectedBrand={selectedBrand}
      />

      <div className="space-y-6">
        {/* Agent and Language Selectors */}
        <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
          {isLoadingAgents ? (
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <Skeleton className="mb-3 h-4 w-24" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
              <div className="w-full sm:w-48">
                <Skeleton className="mb-3 h-4 w-20" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
            </div>
          ) : agents.length === 0 ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
                <Icon name="Bot" className="text-gray-400" size={32} />
              </div>
              <h3 className="mb-2 text-lg font-bold text-white">
                {t("empty.noAgents.title")}
              </h3>
              <p className="mb-4 text-sm text-gray-400">
                {t("empty.noAgents.subtitle")}
              </p>
              <Link
                to="/ai-agents"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-blue-700"
              >
                <Icon name="Plus" size={16} />
                {t("actions.createAgent")}
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              {/* Agent Selector */}
              <div className="w-full sm:flex-1">
                <label
                  htmlFor="agent-selector"
                  className="mb-3 block text-sm font-bold text-white"
                >
                  {t("selectors.agent")}
                </label>
                <select
                  id="agent-selector"
                  value={selectedAgentId || ""}
                  onChange={handleAgentChange}
                  className="custom-select h-11 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 font-medium text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">{t("selectors.selectAgent")}</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {getAgentLabel(agent)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Language Selector */}
              <div className="w-full sm:w-48">
                <label
                  htmlFor="locale-selector"
                  className="mb-3 block text-sm font-bold text-white"
                >
                  {t("selectors.language")}
                </label>
                <select
                  id="locale-selector"
                  value={activeLocale}
                  onChange={handleLocaleChange}
                  className="custom-select h-11 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 font-medium text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  {AVAILABLE_LOCALES.map((locale) => (
                    <option key={locale.code} value={locale.code}>
                      {t(`locales.${locale.code}`, {
                        defaultValue: locale.label,
                      })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Copy from Another Agent Button */}
          {selectedAgentId && agents.length > 0 && (
            <div className="mt-4 border-t border-gray-800 pt-4">
              <button
                type="button"
                onClick={() => setIsCopyModalOpen(true)}
                className="flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-gray-700"
              >
                <Icon name="Copy" size={16} />
                {t("actions.copyFromAnotherAgent")}
              </button>
            </div>
          )}
        </div>

        {/* Content when agent is selected */}
        {selectedAgentId && (
          <>
            {/* Search Bar */}
            {!isLoadingTranslations && translationKeys.length > 0 && (
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <Icon name="Search" className="text-gray-500" size={18} />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("search.placeholder")}
                  className="h-11 w-full rounded-lg border border-gray-800 bg-gray-900 pr-10 pl-11 font-medium text-white placeholder:text-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 transition-colors hover:text-white"
                  >
                    <Icon name="X" size={16} />
                  </button>
                )}
              </div>
            )}

            {/* Search Results Count */}
            {searchQuery && !isLoadingTranslations && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-400">
                  {t("search.results", {
                    shown: filteredTranslationKeys.length,
                    total: translationKeys.length,
                  })}
                </span>
                {filteredTranslationKeys.length === 0 && (
                  <span className="text-amber-500">
                    {t("search.noMatches")}
                  </span>
                )}
              </div>
            )}

            {/* Translations List */}
            {isLoadingTranslations ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-gray-800 bg-gray-900/70 px-4 py-3"
                  >
                    <div className="grid gap-3 md:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_auto] md:items-center">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                      <Skeleton className="h-6 w-24 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : translationKeys.length === 0 ? (
              <div className="rounded-2xl border border-gray-800 bg-gray-900 p-8 text-center">
                <Icon
                  name="MessageSquare"
                  className="mx-auto mb-4 text-gray-400"
                  size={40}
                />
                <h3 className="mb-2 text-lg font-bold text-white">
                  {t("empty.noTranslations.title")}
                </h3>
                <p className="text-sm text-gray-400">
                  {t("empty.noTranslations.subtitle")}
                </p>
              </div>
            ) : filteredTranslationKeys.length === 0 ? (
              <div className="rounded-2xl border border-gray-800 bg-gray-900 p-8 text-center">
                <Icon
                  name="SearchX"
                  className="mx-auto mb-4 text-gray-400"
                  size={40}
                />
                <h3 className="mb-2 text-lg font-bold text-white">
                  {t("empty.noSearchResults.title")}
                </h3>
                <p className="text-sm text-gray-400">
                  {t("empty.noSearchResults.subtitle", {
                    query: searchQuery,
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="mt-4 rounded-lg bg-gray-800 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-gray-700"
                >
                  {t("actions.clearSearch")}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {paginatedTranslationKeys.map((key) => {
                  const translation = translations[key];
                  const currentValue =
                    editedValues[key] ?? translation?.value ?? "";
                  const charCount = currentValue?.length || 0;
                  const maxLength = translation?.maxLength || 500;
                  const isOverLimit = charCount > maxLength;
                  const placeholders = extractPlaceholders(
                    translation?.default,
                  );
                  const isEdited = key in editedValues;
                  const isResetting =
                    resetMutation.isPending &&
                    resetMutation.variables?.key === key;

                  return (
                    <div
                      key={key}
                      data-testid="translation-row"
                      className="rounded-xl border border-gray-800 bg-gray-900/70 px-3 py-3 md:px-4"
                    >
                      <div className="grid gap-3 md:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_auto] md:items-center md:gap-4">
                        <div className="flex min-w-0 items-center gap-2">
                          <h4
                            className="truncate text-sm font-semibold text-white"
                            title={formatKeyLabel(key)}
                          >
                            {formatKeyLabel(key)}
                          </h4>
                          {placeholders.length > 0 && (
                            <span
                              className="rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-semibold text-gray-400"
                              title={`${t("labels.placeholders")} ${placeholders.join(", ")}`}
                            >
                              {t("labels.placeholders")} {placeholders.length}
                            </span>
                          )}
                          {translation?.isCustom && !isEdited && (
                            <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-bold tracking-wider text-blue-400 uppercase">
                              {t("badges.custom")}
                            </span>
                          )}
                          {isEdited && (
                            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold tracking-wider text-amber-400 uppercase">
                              {t("badges.modified")}
                            </span>
                          )}
                        </div>

                        <textarea
                          value={currentValue}
                          onChange={(e) =>
                            handleFieldChange(key, e.target.value)
                          }
                          placeholder={translation?.default || ""}
                          rows={1}
                          className={`h-10 min-h-10 w-full resize-y rounded-lg bg-gray-800 px-3 py-2 text-sm font-medium text-white transition-all placeholder:text-gray-600 focus:ring-2 focus:outline-none ${
                            isOverLimit
                              ? "border-2 border-red-500 focus:ring-red-500"
                              : "border border-gray-700 focus:border-transparent focus:ring-blue-500"
                          }`}
                        />

                        <div className="flex flex-wrap items-center gap-2 md:justify-end">
                          <span
                            className={`text-xs font-semibold ${isOverLimit ? "text-red-500" : "text-gray-500"}`}
                          >
                            {charCount} / {maxLength}
                          </span>
                          {translation?.isCustom && !isEdited && (
                            <button
                              type="button"
                              onClick={() => handleResetField(key)}
                              disabled={isResetting}
                              className="flex items-center gap-1.5 rounded-lg bg-gray-800 px-3 py-1.5 text-xs font-bold text-gray-400 transition-all hover:bg-gray-700 hover:text-white disabled:opacity-50"
                            >
                              {isResetting ? (
                                <Icon
                                  name="Loader2"
                                  className="animate-spin"
                                  size={12}
                                />
                              ) : (
                                <Icon name="RotateCcw" size={12} />
                              )}
                              {t("actions.resetToDefault")}
                            </button>
                          )}
                        </div>
                      </div>

                      {translation?.default &&
                        currentValue !== translation.default && (
                          <p
                            className="mt-2 truncate text-xs text-gray-500"
                            title={`${t("labels.defaultValue")}: ${translation.default}`}
                          >
                            {t("labels.defaultValue")}: {translation.default}
                          </p>
                        )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex flex-col items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 p-4 sm:flex-row">
                <div className="text-sm text-gray-400">
                  {t("pagination.showing", {
                    from: (currentPage - 1) * TRANSLATIONS_PER_PAGE + 1,
                    to: Math.min(
                      currentPage * TRANSLATIONS_PER_PAGE,
                      pagination.total,
                    ),
                    total: pagination.total,
                  })}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.total_pages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={5}
                />
              </div>
            )}

            {/* Spacer for sticky footer */}
            {hasUnsavedChanges && <div className="h-20" />}
          </>
        )}
      </div>

      {/* Sticky Footer Bar for Unsaved Changes */}
      {hasUnsavedChanges && (
        <div className="sticky bottom-0 z-40 -mx-3 border-t border-gray-800 bg-gray-900/95 px-3 shadow-2xl backdrop-blur-sm sm:-mx-4 sm:px-4">
          <div className="py-4 pr-16">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                <span className="text-sm font-medium text-amber-500">
                  {t("unsaved.message")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleDiscardChanges}
                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-gray-700 sm:px-6"
                >
                  {t("actions.discard")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveAll}
                  disabled={
                    updateMutation.isPending ||
                    hasValidationErrors ||
                    Object.keys(editedValues).length === 0
                  }
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6"
                >
                  {updateMutation.isPending && (
                    <Icon name="Loader2" className="animate-spin" size={16} />
                  )}
                  {t("actions.saveChanges")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Translations Modal */}
      {isCopyModalOpen && (
        <CopyTranslationsModal
          isOpen={isCopyModalOpen}
          onClose={() => setIsCopyModalOpen(false)}
          onConfirm={handleCopyFromAgent}
          targetAgent={selectedAgent}
          availableAgents={agents.filter((a) => a.id !== selectedAgentId)}
          isPending={copyMutation.isPending}
          getAgentLabel={getAgentLabel}
          t={t}
        />
      )}
    </div>
  );
};

const CopyTranslationsModal = ({
  isOpen,
  onClose,
  onConfirm,
  targetAgent,
  availableAgents,
  isPending,
  getAgentLabel,
  t,
}) => {
  const [sourceAgentId, setSourceAgentId] = React.useState(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-md rounded-2xl border border-gray-800 bg-gray-900 p-6 shadow-2xl">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            {t("modal.copy.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="mb-6">
          <p className="mb-3 text-sm text-gray-400">
            {t("modal.copy.subtitle")}
          </p>
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
            <div className="flex items-start gap-2">
              <Icon
                name="AlertTriangle"
                className="mt-0.5 flex-shrink-0 text-red-400"
                size={16}
              />
              <p className="text-sm text-red-400">
                <span className="font-bold">
                  {t("modal.copy.warningLabel")}
                </span>{" "}
                {t("modal.copy.warningStart")}{" "}
                <span className="font-bold">
                  {t("modal.copy.warningReplace")}
                </span>{" "}
                {t("modal.copy.warningMiddle")}{" "}
                <span className="font-bold text-white">
                  {getAgentLabel(targetAgent)}
                </span>
                . {t("modal.copy.warningEnd")}
              </p>
            </div>
          </div>
        </div>

        {availableAgents.length === 0 ? (
          <div className="py-6 text-center">
            <Icon
              name="AlertCircle"
              className="mx-auto mb-3 text-gray-400"
              size={32}
            />
            <p className="text-sm text-gray-400">{t("modal.copy.noAgents")}</p>
          </div>
        ) : (
          <>
            <label
              htmlFor="source-agent"
              className="mb-3 block text-sm font-bold text-white"
            >
              {t("modal.copy.sourceAgent")}
            </label>
            <select
              id="source-agent"
              value={sourceAgentId || ""}
              onChange={(e) =>
                setSourceAgentId(
                  e.target.value ? parseInt(e.target.value, 10) : null,
                )
              }
              className="custom-select mb-6 h-11 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 font-medium text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="">{t("selectors.selectAgent")}</option>
              {availableAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {getAgentLabel(agent)}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg bg-gray-800 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-gray-700"
              >
                {t("actions.cancel")}
              </button>
              <button
                type="button"
                onClick={() => onConfirm(sourceAgentId)}
                disabled={!sourceAgentId || isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isPending && (
                  <Icon name="Loader2" className="animate-spin" size={16} />
                )}
                {t("actions.copyTranslations")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgentTranslationsSettingsPage;
